/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FontIcon } from '@bingads-webui-react/fabric-control';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { Link, usePageManager } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { Pagination } from './pagination';
import { DisplayAdPanel } from './display-ad-panel';
import { getAdsStepStyles } from './styles';

export const DisplayAdsContainer = (props) => {
  const {
    aid,
    cid,
    i18n,
    perfMarker,
    onAdsValidation,
    currentImageIndex,
    ads = [],
    oDataConfig,
    disablePagination,
    permissions,
    customStyleFunction,
    useCustomStyleFunction,
    resetFields,
    isAdGridEditorMode,
    campaignId,
    isLoadingImage,
    initialAdsCount,
  } = props;

  const activity = useContext(ActivityContext);

  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const customClasses = useDefaultStyles(customStyleFunction);

  const classes = useCustomStyleFunction ? customClasses : defaultClasses;

  const [DisplayAds, setDisplayAds] = useState(ads || []);

  const {
    goToNextPage,
    goToPrevPage,
    goToPage,
    getPageAdjustedIndex,
    currentPage,
    currentPageIndex,
    pages,
    onAddItem,
    removeItem,
    totalPages,
    maxItems,
  } = usePageManager(ads, initialAdsCount);

  const onAddAd = useCallback(() => {
    onAddItem();
    if (activity) {
      activity.signal(`Adding new ad panel - current total: ${currentPage.numberOfAdsInPage}`);
    }
  }, [activity, currentPage.numberOfAdsInPage, onAddItem]);

  const onImagePanelUpdate = (result, idx, uid) => {
    let itemAtIdx = DisplayAds[idx] || { image: null, FinalUrls: [], uid: null };

    if (!itemAtIdx.uid) {
      itemAtIdx = { ...result, uid, Id: itemAtIdx.Id };
    } else {
      itemAtIdx = { ...result, uid: itemAtIdx.uid, Id: itemAtIdx.Id };
    }

    const itemsBefore = DisplayAds.slice(0, idx);
    const itemsAfter = DisplayAds.slice(idx + 1, DisplayAds.length);
    setDisplayAds([...itemsBefore, { ...itemAtIdx }, ...itemsAfter]);
  };

  const onRemovePanel = (idx) => {
    if (activity) {
      activity.signal(`Removing ad panel at idx ${idx}`);
    }
    DisplayAds.splice(idx, 1);

    setDisplayAds([...DisplayAds]);

    removeItem();
  };

  const isAllValid = () => {
    let valid = true;
    // eslint-disable-next-line consistent-return
    _.each(DisplayAds, (ad) => {
      if (!ad.isValid) {
        valid = false;
      }
    });

    return valid;
  };

  const isAllDirty = () => {
    let dirty = false;
    // eslint-disable-next-line consistent-return
    _.each(DisplayAds, (ad) => {
      if (ad.isDirty) {
        dirty = true;
      }
    });

    return dirty;
  };

  useEffect(() => {
    const result = {
      data: [...DisplayAds], isValid: isAllValid(), isDirty: isAllDirty(), currentPageIndex,
    };
    onAdsValidation(result);
    if (activity) {
      activity.signal(`onDisplayAdsContainerValidated - ${JSON.stringify(result)}`);
    }
  // disabling exhaustive deps since the onAdsValidation value changes on each rerender.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIndex, DisplayAds]);

  useEffect(() => {
    if (resetFields) {
      setDisplayAds([]);
    }
  }, [resetFields]);

  useEffect(() => {
    setClarityTag('XandrDisplayAds', 'true');
  }, []);

  useEffect(() => {
    if (!_.isEqual(ads, DisplayAds) && !isAdGridEditorMode) {
      // check if ads has any ad that has id. (update scenario)
      let shouldUpdate = false;

      _.each(ads, (ad) => {
        const displayAd = _.filter(DisplayAds, vAd => vAd.uid === ad.uid);

        if (displayAd && displayAd[0] && _.isUndefined(displayAd[0].Id) && ad.Id) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setDisplayAds(ads);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  return (
    <div className={classes.rootDisplayAdsContainer}>
      {
        _.map(pages, (page) => {
          const panels = _.map(_.range(page.numberOfAdsInPage), (idx) => {
            const displayAd = (DisplayAds[getPageAdjustedIndex(idx, page.pageNumber)] && DisplayAds[getPageAdjustedIndex(idx, page.pageNumber)].data);

            const uid = (DisplayAds[getPageAdjustedIndex(idx, page.pageNumber)] && DisplayAds[getPageAdjustedIndex(idx, page.pageNumber)].uid) || _.uniqueId('displayAd_');

            const isCurrentPage = page.id === currentPage.id;

            return (
              <div style={{ display: isCurrentPage ? 'block' : 'none' }} className={`image-panel-idx-${idx}`}>
                <DisplayAdPanel
                  key={`${uid}-imagePanel`}
                  i18n={i18n}
                  perfMarker={perfMarker}
                  cid={cid}
                  aid={aid}
                  campaignId={campaignId}
                  permissions={permissions}
                  displayAd={displayAd}
                  onUpdate={result => onImagePanelUpdate(result, getPageAdjustedIndex(idx, page.pageNumber), uid)}
                  onRemovePanel={() => onRemovePanel(getPageAdjustedIndex(idx, page.pageNumber), page)}
                  removeCloseButton={page.numberOfAdsInPage === 1}
                  keyIndex={uid}
                  isSelectedPanel={currentImageIndex === idx}
                  oDataConfig={oDataConfig}
                  isLoadingImage={isLoadingImage}
                />
              </div>
            );
          });

          return panels;
        })
      }
      {
        (currentPage.numberOfAdsInPage < maxItems && !disablePagination && !_.isEmpty(DisplayAds)) &&
        <div style={{ marginTop: 10 }}>
          <Link className="add-new-ad" onClick={onAddAd}>
            <FontIcon iconName="Add" />&nbsp;{i18n.getString(_TL_('Create another ad'))}
          </Link>
        </div>
      }
      <Pagination
        totalPages={totalPages}
        disablePagination={disablePagination}
        classes={classes}
        goToPrevPage={goToPrevPage}
        goToNextPage={goToNextPage}
        goToPage={goToPage}
        currentPageIndex={currentPageIndex}
      />
    </div>
  );
};

DisplayAdsContainer.propTypes = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  campaignId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  onAdsValidation: PropTypes.func.isRequired,
  ads: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  oDataConfig: PropTypes.shape({}),
  disablePagination: PropTypes.bool,
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
  resetFields: PropTypes.bool,
  currentImageIndex: PropTypes.number,
  isAdGridEditorMode: PropTypes.bool,
  isLoadingImage: PropTypes.bool,
  initialAdsCount: PropTypes.number,
};

DisplayAdsContainer.defaultProps = {
  oDataConfig: null,
  disablePagination: false,
  customStyleFunction: _.noop,
  useCustomStyleFunction: false,
  resetFields: false,
  currentImageIndex: 0,
  isAdGridEditorMode: false,
  isLoadingImage: false,
  initialAdsCount: 0,
};
